import type { ObjectPermission, Role, SearchPermissions } from '../providers/roleProvider';

export type ApplicationAccess =
  | 'regal_main'
  | 'cybersweep'
  | 'credit_block'
  | 'credit_management';

export interface MergedPermissionSummary {
  roles: Role[];
  roleNames: string[];
  primaryRole: Role | null;
  isSystemAdmin: boolean;
  permissions: ObjectPermission[];
  searchPermissions: SearchPermissions;
  accessibleApps: ApplicationAccess[];
  canEdit: boolean;
  canEditCyberSweep: boolean;
  canEditCreditBlock: boolean;
  canEditCreditManagement: boolean;
}

const SEARCH_KEYS: Array<keyof Omit<SearchPermissions, 'visibleCategories'>> = [
  'canSearchObjects',
  'canSearchFields',
  'canSearchWorkflows',
  'canSearchSystemSettings',
  'canSearchUsers',
  'canSearchRoles',
  'canSearchSecurity',
  'canSearchReports',
  'canSearchDashboards',
  'canSearchProfileSettings',
  'canSearchClients',
  'canSearchContacts',
  'canSearchLeads',
  'canSearchOpportunities',
  'canSearchCases',
  'canSearchFirms',
  'canSearchCyberSweepClients',
  'canSearchCSAudits',
  'canSearchPAWRecords',
  'canSearchCreditCases',
  'canSearchTradelines',
  'canSearchCreditBlockAccounts',
];

const DEFAULT_SEARCH_PERMISSIONS: SearchPermissions = {
  canSearchObjects: false,
  canSearchFields: false,
  canSearchWorkflows: false,
  canSearchSystemSettings: false,
  canSearchUsers: false,
  canSearchRoles: false,
  canSearchSecurity: false,
  canSearchReports: false,
  canSearchDashboards: false,
  canSearchProfileSettings: true,
  canSearchClients: false,
  canSearchContacts: false,
  canSearchLeads: false,
  canSearchOpportunities: false,
  canSearchCases: false,
  canSearchFirms: false,
  canSearchCyberSweepClients: false,
  canSearchCSAudits: false,
  canSearchPAWRecords: false,
  canSearchCreditCases: false,
  canSearchTradelines: false,
  canSearchCreditBlockAccounts: false,
  visibleCategories: ['profile'],
};

const ROLE_PRIORITY: string[] = [
  'System Administrator',
  'Operations Manager',
  'Credit Block Team Lead',
  'CyberSweep Team Lead',
  'Credit Block Processor',
  'CyberSweep Auditor',
  'Data Analyst',
];

function compareRolePriority(a: Role, b: Role): number {
  const aIndex = ROLE_PRIORITY.indexOf(a.name);
  const bIndex = ROLE_PRIORITY.indexOf(b.name);
  const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
  const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
  if (aRank !== bRank) return aRank - bRank;
  return a.name.localeCompare(b.name);
}

function dedupeApps(apps: ApplicationAccess[]): ApplicationAccess[] {
  return Array.from(new Set(apps));
}

function objectNameMatches(permission: ObjectPermission, fragments: string[]): boolean {
  const name = String(permission.objectName ?? '').toLowerCase();
  return fragments.some((fragment) => name.includes(fragment));
}

export function getPrimaryRole(roles: Role[] | null | undefined): Role | null {
  if (!roles?.length) return null;
  return [...roles].sort(compareRolePriority)[0] ?? null;
}

export function mergeObjectPermissions(roles: Role[] | null | undefined): ObjectPermission[] {
  if (!roles?.length) return [];

  const merged = new Map<string, ObjectPermission>();
  for (const role of roles) {
    for (const permission of role.permissions ?? []) {
      const key = permission.objectId || permission.objectName;
      const current = merged.get(key);
      if (!current) {
        merged.set(key, { ...permission });
        continue;
      }
      merged.set(key, {
        ...current,
        objectName: current.objectName || permission.objectName,
        read: current.read || permission.read,
        write: current.write || permission.write,
        delete: current.delete || permission.delete,
      });
    }
  }

  return Array.from(merged.values()).sort((a, b) => a.objectName.localeCompare(b.objectName));
}

export function mergeSearchPermissions(roles: Role[] | null | undefined): SearchPermissions {
  if (!roles?.length) return { ...DEFAULT_SEARCH_PERMISSIONS };

  const merged: SearchPermissions = {
    ...DEFAULT_SEARCH_PERMISSIONS,
    visibleCategories: [...DEFAULT_SEARCH_PERMISSIONS.visibleCategories],
  };
  const visible = new Set<SearchPermissions['visibleCategories'][number]>(merged.visibleCategories);

  for (const role of roles) {
    const search = role.searchPermissions;
    if (!search) continue;
    for (const key of SEARCH_KEYS) {
      merged[key] = merged[key] || Boolean(search[key]);
    }
    for (const category of search.visibleCategories ?? []) {
      visible.add(category);
    }
  }

  merged.visibleCategories = Array.from(visible);
  return merged;
}

export function summarizeRoles(roles: Role[] | null | undefined): MergedPermissionSummary {
  const safeRoles = roles ?? [];
  const roleNames = safeRoles.map((role) => role.name);
  const primaryRole = getPrimaryRole(safeRoles);
  const isSystemAdmin = safeRoles.some((role) => role.isSystem || role.name === 'System Administrator');

  if (isSystemAdmin) {
    return {
      roles: safeRoles,
      roleNames,
      primaryRole,
      isSystemAdmin: true,
      permissions: mergeObjectPermissions(safeRoles),
      searchPermissions: {
        ...DEFAULT_SEARCH_PERMISSIONS,
        ...SEARCH_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        visibleCategories: [
          'objects', 'workflows', 'fields', 'settings',
          'reports', 'dashboards', 'profile',
          'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms',
        ],
      },
      accessibleApps: ['regal_main', 'cybersweep', 'credit_block', 'credit_management'],
      canEdit: true,
      canEditCyberSweep: true,
      canEditCreditBlock: true,
      canEditCreditManagement: true,
    };
  }

  const permissions = mergeObjectPermissions(safeRoles);
  const searchPermissions = mergeSearchPermissions(safeRoles);
  const hasWritePermission = permissions.some((permission) => permission.write);
  const canEditCyberSweep = permissions.some((permission) =>
    permission.write && objectNameMatches(permission, ['cybersweep', 'audit', 'paw'])
  );
  const canEditCreditBlock = permissions.some((permission) =>
    permission.write && objectNameMatches(permission, ['creditblock', 'credit_block', 'credit_bureaus', 'unlock_request', 'unlock'])
  );
  const canEditCreditManagement = permissions.some((permission) =>
    permission.write && objectNameMatches(permission, ['tradeline', 'creditcase', 'credit_case'])
  );

  const accessibleApps = dedupeApps([
    'regal_main',
    ...(searchPermissions.canSearchCyberSweepClients || searchPermissions.canSearchCSAudits ? ['cybersweep' as const] : []),
    ...(searchPermissions.canSearchCreditBlockAccounts ? ['credit_block' as const] : []),
    ...(searchPermissions.canSearchCreditCases || searchPermissions.canSearchTradelines ? ['credit_management' as const] : []),
  ]);

  return {
    roles: safeRoles,
    roleNames,
    primaryRole,
    isSystemAdmin: false,
    permissions,
    searchPermissions,
    accessibleApps,
    canEdit: hasWritePermission,
    canEditCyberSweep: canEditCyberSweep || (searchPermissions.canSearchCyberSweepClients && hasWritePermission),
    canEditCreditBlock: canEditCreditBlock || (searchPermissions.canSearchCreditBlockAccounts && hasWritePermission),
    canEditCreditManagement: canEditCreditManagement || (searchPermissions.canSearchCreditCases && hasWritePermission),
  };
}

export function getAppViewRole(
  app: ApplicationAccess,
  roles: Role[] | null | undefined,
  keycloakRoles: string[] = []
): string {
  const roleNames = new Set((roles ?? []).map((role) => role.name));
  const keycloak = new Set(keycloakRoles);

  const isSystemAdmin = roleNames.has('System Administrator') || keycloak.has('system_admin');
  const isOpsManager = roleNames.has('Operations Manager') || keycloak.has('manager');
  const isSystemManager = isSystemAdmin || isOpsManager;

  if (app === 'cybersweep') {
    if (isSystemManager || roleNames.has('CyberSweep Team Lead') || keycloak.has('cs_supervisor')) return 'manager';
    if (roleNames.has('CyberSweep Auditor') || keycloak.has('cs_auditor')) return 'cs_auditor';
    if (roleNames.has('Data Analyst') || keycloak.has('data_analyst')) return 'data_analyst';
    return 'employee';
  }

  if (app === 'credit_block') {
    if (isSystemManager || roleNames.has('Credit Block Team Lead') || keycloak.has('cb_supervisor')) return 'manager';
    if (roleNames.has('Credit Block Processor') || keycloak.has('cb_team')) return 'cb_team';
    if (roleNames.has('Data Analyst') || keycloak.has('data_analyst')) return 'data_analyst';
    return 'employee';
  }

  if (app === 'credit_management') {
    if (isSystemManager || keycloak.has('cm_supervisor')) return 'manager';
    return 'employee';
  }

  if (isSystemAdmin) return 'system_admin';
  if (isOpsManager) return 'manager';
  return 'employee';
}
